import { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

export interface Rider {
  _id: string;
  name: string;
  title?: string;
  shortTitle: string;
  description: string;
  shortDescription: string;
  price: number;
  pricePer: string;
  imageUrl: string[];
  currencySymbol: string;
  slug: string;
  duration: string;
  attractions: string;
  category: string;
  rating: number;
  review: number;
  totalDistance: number;
  totalHoursTo: number;
  totalHoursFrom: number;
}

interface UseRidersPaginatedResult {
  riders: Rider[];
  currentPage: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  search: string;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;
  totalCount: number;
}

interface RequestBody {
  queryParams: {
    limit: number;
    offset: number;
    orderBy: string;
    orderIn: string;
    page: number;
    search?: string;
  };
  targetCurrencyCode?: string;
  targetCurrencySymbol?: string;
}

const fetchRidersPage = async (
  page: number,
  search: string,
  pageSize: number = 12
) => {
  if (search.trim().length > 0 && search.trim().length < 3) {
    return { list: [], hasMore: false };
  }

  const requestBody: RequestBody = {
    queryParams: {
      limit: pageSize,
      offset: (page - 1) * pageSize,
      orderBy: 'createdAt',
      orderIn: 'desc',
      page: page,
      ...(search.trim().length >= 3 && { search: search.trim() }),
    },
  };

  const response = await axios.post(
    `${process.env.NEXT_PUBLIC_API_URL}/travel/riders-corner/v1/list-all`,
    requestBody
  );

  const riders: Rider[] = response.data.data?.list || [];
  const hasMore = riders.length === pageSize;

  return { list: riders, hasMore };
};

export const useRidersPaginated = (): UseRidersPaginatedResult => {
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 12;

  // Debounce search - only search if length is 0, 2, or >= 3
  const shouldSearch = useMemo(() => {
    return search.length === 0 || search.length === 2 || search.length >= 3;
  }, [search]);

  // Reset to page 1 when search changes
  const handleSearchChange = useCallback((newSearch: string) => {
    setCurrentPage(1);
    setTotalPages(1); // Reset total pages
    setSearch(newSearch);
  }, []);

  // Fetch all pages to determine total count
  const { data: allPagesData, isLoading: isLoadingDiscovery } = useQuery({
    queryKey: ['riders-corner', 'discovery', search.trim()],
    queryFn: async () => {
      if (!shouldSearch) return { totalPages: 1, pages: [] };

      let discoveredPages = 1;
      let hasMore = true;
      let pageNumber = 1;
      const pages: Array<{ page: number; hasMore: boolean; list: Rider[] }> =
        [];

      // Keep fetching pages until we find one that's empty
      while (hasMore && pageNumber <= 20) {
        // Safety limit of 20 pages
        const result = await fetchRidersPage(pageNumber, search, pageSize);
        pages.push({
          page: pageNumber,
          hasMore: result.hasMore,
          list: result.list,
        });

        if (result.list.length === 0) {
          hasMore = false;
        } else if (result.hasMore) {
          discoveredPages++;
          pageNumber++;
        } else {
          discoveredPages = pageNumber;
          hasMore = false;
        }
      }

      return { totalPages: discoveredPages, pages };
    },
    enabled: shouldSearch,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  // Update total pages when discovery completes
  useEffect(() => {
    if (allPagesData) {
      setTotalPages(allPagesData.totalPages);
    }
  }, [allPagesData]);

  // Fetch current page data
  const {
    data: currentPageData,
    isLoading: isLoadingCurrent,
    error: currentError,
  } = useQuery({
    queryKey: ['riders-corner', 'paginated', search.trim(), currentPage],
    queryFn: () => fetchRidersPage(currentPage, search, pageSize),
    enabled: shouldSearch,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });

  const riders = currentPageData?.list || [];
  const isLoading = isLoadingDiscovery || isLoadingCurrent;
  const error = currentError
    ? currentError instanceof Error
      ? currentError.message
      : 'Failed to load riders.'
    : null;

  const setPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
      }
    },
    [totalPages]
  );

  return {
    riders,
    currentPage,
    totalPages,
    isLoading,
    error,
    search,
    setSearch: handleSearchChange,
    setPage,
    totalCount: totalPages * pageSize,
  };
};
